import { useState, useEffect } from 'react';
import type React from 'react';
import { FormInput, SlideModal } from '@podus/ui';
import { MdAdd, MdDeleteOutline } from 'react-icons/md';
import { api } from '@/lib/api';
import type { FeeConfig } from '@/types';

interface BreakdownEntry {
    name: string;
    price: string;
}

interface UpdatePaymentModalProps {
    open: boolean;
    onClose: () => void;
    config: FeeConfig;
    onUpdate: () => void;
}

function UpdatePaymentModal({ open, onClose, config, onUpdate }: UpdatePaymentModalProps) {
    const [entries, setEntries] = useState<BreakdownEntry[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && config) {
            setEntries(
                config.items.map((item) => ({
                    name: item.label,
                    price: item.amount.toString(),
                })),
            );
        }
    }, [open, config]);

    const updateEntry = (index: number, field: keyof BreakdownEntry, value: string) => {
        setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, [field]: value } : e)));
    };

    const addEntry = () => {
        setEntries((prev) => [...prev, { name: '', price: '' }]);
    };

    const removeEntry = (index: number) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const items = entries
                .filter((e) => e.name && e.price)
                .map((e) => ({
                    label: e.name,
                    amount: Number(e.price),
                }));

            await api.put(`/api/settings/fees/${config.caseCategory}`, { items });
            onUpdate();
            onClose();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Failed to update fees');
        } finally {
            setLoading(false);
        }
    };

    const computedTotal = entries.reduce((sum, e) => sum + (Number(e.price) || 0), 0);

    return (
        <SlideModal open={open} onClose={onClose} title={`${config.caseCategory} Payment Details`}>
            <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-y-auto px-6 pt-6">
                <div className="space-y-4">
                    {entries.map((entry, i) => (
                        <div
                            key={i}
                            className="relative space-y-4 rounded-lg border border-[#E0E0E0] bg-[#F7F7F7] p-4"
                        >
                            {entries.length > 1 && (
                                <button
                                    type="button"
                                    title="Remove fee"
                                    onClick={() => removeEntry(i)}
                                    className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50"
                                >
                                    <MdDeleteOutline size={18} />
                                </button>
                            )}
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                                <FormInput
                                    title={i === 0 ? 'Service Fee' : `Service Fee ${i + 1}`}
                                    id={`fee-name-${i}`}
                                    placeholder={`Enter name of Service Fee ${i + 1}`}
                                    value={entry.name}
                                    onChange={(e) => updateEntry(i, 'name', e.target.value)}
                                    variant="white"
                                />
                                <FormInput
                                    title="Price"
                                    id={`fee-price-${i}`}
                                    type="number"
                                    placeholder="Enter Price"
                                    value={entry.price}
                                    onChange={(e) => updateEntry(i, 'price', e.target.value)}
                                    variant="white"
                                />
                            </div>
                            {i === entries.length - 1 && (
                                <div className="flex flex-wrap items-center justify-start gap-3">
                                    <button
                                        type="button"
                                        onClick={addEntry}
                                        className="flex items-center justify-center gap-2 rounded-lg border border-[#eee] bg-[#E0E0E0] px-6 py-3 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#E0E0E0]/70"
                                    >
                                        <MdAdd size={18} />
                                        Add New Service Fee
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}

                    {computedTotal > 0 && (
                        <div className="rounded-lg bg-[#f5f5f5] px-4 py-3">
                            <p className="text-[13px] text-[#888]">Breakdown Total</p>
                            <p className="mt-1 text-lg font-bold text-secondary">₦{computedTotal.toLocaleString()}</p>
                        </div>
                    )}
                </div>

                <div className="mt-auto pb-6 pt-8">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400 disabled:opacity-50"
                    >
                        {loading ? 'Updating...' : 'Update Payment Details'}
                    </button>
                </div>
            </form>
        </SlideModal>
    );
}

interface PaymentDetailsCardProps {
    config: FeeConfig;
    onUpdate: () => void;
}

export default function PaymentDetailsCard({ config, onUpdate }: PaymentDetailsCardProps) {
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <>
            <div className="rounded-xl border border-[#eee] bg-white p-6">
                <h3 className="mb-4 text-lg font-bold text-secondary">{config.caseCategory} Case Payment Details</h3>

                <div className="rounded-lg border border-[#eee]">
                    <div className="border-b border-[#eee] px-4 py-3">
                        <p className="text-[15px] font-bold text-secondary">Fee Breakdown</p>
                    </div>
                    <div className="divide-y divide-background">
                        {config.items.map((fee, i) => (
                            <div key={i} className="flex items-center justify-between px-4 py-3">
                                <span className="text-[14px] text-[#888]">{fee.label}</span>
                                <span className="text-[14px] font-medium text-secondary">
                                    ₦{fee.amount.toLocaleString()}
                                </span>
                            </div>
                        ))}
                        {config.items.length === 0 && (
                            <div className="px-4 py-3 text-center text-[14px] text-[#aaa]">No fees configured</div>
                        )}
                    </div>
                </div>

                <div className="mt-4 rounded-lg bg-[#f5f5f5] px-4 py-3">
                    <p className="text-[13px] text-[#888]">Total Fee</p>
                    <p className="mt-1 text-2xl font-bold text-secondary">₦{config.totalFee.toLocaleString()}</p>
                </div>

                <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="mt-4 w-full rounded-lg bg-secondary py-3 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400"
                >
                    Update Payment Details
                </button>
            </div>

            <UpdatePaymentModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                config={config}
                onUpdate={onUpdate}
            />
        </>
    );
}
